<template>
	<div class="container mx-auto px-4">
		<div class="flex justify-center">
			<div class="w-full max-w-md">
				<div class="space-y-6">
					<div>
						<h1 class="text-2xl font-bold">Log in</h1>
						<UAlert
							v-if="signinError"
							color="warning"
							variant="subtle"
							class="mt-4"
							:title="'Error'"
							:description="signinError"
						/>
					</div>

					<form
						class="w-full"
						@submit.prevent
					>
						<div
							v-show="!sent"
							class="space-y-4"
						>
							<UFormField label="Phone number">
								<div class="flex flex-wrap gap-2">
									<PhoneNumber
										v-model="phoneNumber"
										class="flex-1"
										autocomplete="username webauthn"
										country_code_name="country_code"
										phone_number_name="phone_number"
										@submit="sendCode"
									/>
									<UButton
										color="primary"
										:disabled="!phoneOutput"
										:loading="submitting"
										@click="sendCode"
									>Continue</UButton>
								</div>
							</UFormField>

							<template v-if="browserSupportsWebAuthnState">
								<hr class="my-4 border-default" >

								<UFormField label="Or: passkey">
									<div class="flex gap-2">
										<UButton
											color="primary"
											variant="soft"
											:disabled="!generatePublicKeyCredentialAuthentication"
											icon="i-heroicons-key"
											@click="onLoginWithPasskeyClick"
										>Log in with a passkey</UButton>
									</div>
								</UFormField>
							</template>
						</div>

						<div
							v-show="sent"
							class="space-y-4"
						>
							<UFormField label="Code">
								<UInput
									ref="codeInput"
									v-model.trim="code"
									type="tel"
									:loading="submitting"
									autocomplete="one-time-code"
									name="code"
								/>
							</UFormField>
							<UButton
								variant="ghost"
								@click="sent = false"
							>Resend</UButton>
						</div>
					</form>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { parsePhoneNumberFromString, type E164Number } from "libphonenumber-js";
import {
	startRegistration,
	startAuthentication,
	type PublicKeyCredentialCreationOptionsJSON,
	type PublicKeyCredentialRequestOptionsJSON,
	WebAuthnError,
	WebAuthnAbortService,
	browserSupportsWebAuthn,
	type AuthenticationResponseJSON,
} from "@simplewebauthn/browser";

import {
	SendPhoneAccessCodeDocument,
	SignInWithPhoneAccessCodeDocument,
	GeneratePublicKeyCredentialRegistrationDocument,
	VerifyPublicKeyCredentialRegistrationDocument,
	GeneratePublicKeyCredentialAuthenticationDocument,
	VerifyPublicKeyCredentialAuthenticationDocument,
	type GeneratePublicKeyCredentialAuthenticationMutation,
} from "~/gql/graphql";

definePageMeta({
	layout: "login",
});

useHead({
	title: "Login",
});

const route = useRoute();
const router = useRouter();
const { onLogin } = useAuth();
const { reportError } = useErrorReporter();
const { sendPasskeySignals } = usePasskeySignals();


const { mutate: sendPhoneAccessCodeMutate } = useMutation(SendPhoneAccessCodeDocument);
const { mutate: signInWithPhoneAccessCodeMutate } = useMutation(SignInWithPhoneAccessCodeDocument);
const { mutate: generatePublicKeyCredentialRegistrationMutate } = useMutation(GeneratePublicKeyCredentialRegistrationDocument);
const { mutate: verifyPublicKeyCredentialRegistrationMutate } = useMutation(VerifyPublicKeyCredentialRegistrationDocument);
const { mutate: generatePublicKeyCredentialAuthenticationMutate } = useMutation(GeneratePublicKeyCredentialAuthenticationDocument);
const { mutate: verifyPublicKeyCredentialAuthenticationMutate } = useMutation(VerifyPublicKeyCredentialAuthenticationDocument);

type GeneratePublicKeyCredentialAuthenticationResult = NonNullable<GeneratePublicKeyCredentialAuthenticationMutation["generatePublicKeyCredentialAuthentication"]>;

// State
const redirect = ref<string | null>((route.query.redirect as string) || null);
const phoneNumber = ref("");
const code = ref("");
const submitting = ref(false);
const sent = ref(false);
const signinError = ref("");
const browserSupportsWebAuthnState = ref(false);
const generatePublicKeyCredentialAuthentication = ref<GeneratePublicKeyCredentialAuthenticationResult | null>(null);
const codeInput = useTemplateRef<{ $el: HTMLElement }>("codeInput");

const phoneOutput = computed<E164Number | null>(() => {
	try {
		const number = parsePhoneNumberFromString(phoneNumber.value, "CA");
		if (!number || !number.isValid()) {
			throw new Error("Invalid number");
		}
		return number.number;
	} catch {
		return null;
	}
});

// Watch code for auto-submit
watch(code, async (newCode) => {
	if (newCode.length === 6) {
		await signInWithCode(newCode);
	}
});

async function sendCode() {
	const phone = phoneOutput.value;
	if (!phone) return;

	signinError.value = "";
	submitting.value = true;

	try {
		await sendPhoneAccessCodeMutate({
			phoneNumber: phone,
		});
	} catch {
		signinError.value = "There was an issue in sending you an SMS. Please check your phone number and try again.";
		return;
	} finally {
		submitting.value = false;
	}

	sent.value = true;
	await nextTick();
	codeInput.value?.$el.querySelector("input")?.focus();
}

async function signInWithCode(codeValue: string) {
	const phone = phoneOutput.value;
	if (!phone || !codeValue) return;

	submitting.value = true;

	try {
		const result = await signInWithPhoneAccessCodeMutate({
			phoneNumber: phone,
			accessCode: codeValue,
		});

		if (!result?.data?.signInWithPhoneAccessCode) {
			signinError.value = "Unable to verify your code. Please check your code and try again.";
			return;
		}

		const jwt = result.data.signInWithPhoneAccessCode;

		try {
			await onLogin(jwt);
		} catch (e: unknown) {
			reportError(e);
			return;
		}

		// an SMS sign-in syncs passkeys too, and the RP ID comes from the options fetched for passkey autofill
		const requestOptions = generatePublicKeyCredentialAuthentication.value
			?.publicKeyCredentialRequestOptions as unknown as PublicKeyCredentialRequestOptionsJSON | undefined;

		void sendPasskeySignals(requestOptions?.rpId);

		if (browserSupportsWebAuthnState.value) {
			await router.push({
				path: "/login/upgrade-to-passkey",
				query: {
					redirect: redirect.value,
				},
			});
		} else {
			await router.push(redirect.value || "/");
		}
	} catch {
		signinError.value = "There was an issue in verifying your code. Please try again.";
		code.value = "";
	} finally {
		submitting.value = false;
	}
}

async function onLoginWithPasskeyClick(_event: MouseEvent) {
	await performWebAuthnAuthentication();
}

async function fetchWebAuthnAuthenticationOptions() {
	generatePublicKeyCredentialAuthentication.value = null;

	const result = await generatePublicKeyCredentialAuthenticationMutate();
	generatePublicKeyCredentialAuthentication.value = result?.data?.generatePublicKeyCredentialAuthentication ?? null;
}

async function performWebAuthnAutoregister() {
	const result = await generatePublicKeyCredentialRegistrationMutate();
	const registration = result?.data?.generatePublicKeyCredentialRegistration;

	if (!registration) {
		throw new Error("Missing generatePublicKeyCredentialRegistration");
	}

	const optionsJSON = registration.publicKeyCredentialCreationOptions as unknown as PublicKeyCredentialCreationOptionsJSON;

	let registrationResponse;

	try {
		registrationResponse = await startRegistration({
			optionsJSON,
			useAutoRegister: true,
		});
	} catch (e) {
		if (e instanceof WebAuthnError && (e.code === "ERROR_CEREMONY_ABORTED" || e.name === "NotAllowedError")) {
			return;
		} else if (e instanceof Error && e.name === "AbortError") {
			return;
		} else {
			reportError(e);
			return;
		}
	}

	const verificationResult = await verifyPublicKeyCredentialRegistrationMutate({
		input: {
			registrationResponse: registrationResponse as unknown as Record<string, unknown>,
		},
	});

	if (!verificationResult?.data?.verifyPublicKeyCredentialRegistration.verified) {
		reportError(new Error("Failed to verify registration"));
	}
}

async function performWebAuthnAuthentication() {
	const authOptions = generatePublicKeyCredentialAuthentication.value;

	if (!authOptions) {
		throw new Error("Missing generatePublicKeyCredentialAuthentication");
	}

	const optionsJSON = authOptions.publicKeyCredentialRequestOptions as unknown as PublicKeyCredentialRequestOptionsJSON;
	const requestId = authOptions.requestId;

	let authenticationResponse;

	try {
		authenticationResponse = await startAuthentication({
			optionsJSON,
		});
	} catch (e) {
		await fetchWebAuthnAuthenticationOptions();

		if (e instanceof WebAuthnError && (e.code === "ERROR_CEREMONY_ABORTED" || e.name === "NotAllowedError")) {
			return;
		} else if (e instanceof Error && e.name === "AbortError") {
			return;
		} else {
			reportError(e);
			return;
		}
	}

	submitting.value = true;

	const verificationResult = await verifyPublicKeyCredentialAuthenticationMutate({
		input: {
			authenticationResponse: authenticationResponse as unknown as Record<string, unknown>,
			requestId,
		},
	});

	const jwt = verificationResult?.data?.verifyPublicKeyCredentialAuthentication.jwt;

	if (!jwt) {
		throw new Error("Authentication failed");
	}

	try {
		await onLogin(jwt);
	} catch (e: unknown) {
		reportError(e);
		return;
	} finally {
		submitting.value = false;
	}

	if (authenticationResponse.authenticatorAttachment === "cross-platform") {
		submitting.value = true;
		await performWebAuthnAutoregister();
	}

	void sendPasskeySignals(optionsJSON.rpId);

	await router.push(redirect.value || "/");
}

async function performWebAuthnAutoFill() {
	const authOptions = generatePublicKeyCredentialAuthentication.value;

	if (!authOptions) {
		throw new Error("Missing generatePublicKeyCredentialAuthentication");
	}

	const optionsJSON = authOptions.publicKeyCredentialRequestOptions as unknown as PublicKeyCredentialRequestOptionsJSON;
	const requestId = authOptions.requestId;

	let retries = 0;

	const startAuthenticationLoop = async (): Promise<AuthenticationResponseJSON | null> => {
		try {
			return await startAuthentication({
				optionsJSON,
				useBrowserAutofill: true,
			});
		} catch (e) {
			await fetchWebAuthnAuthenticationOptions();

			if (e instanceof WebAuthnError && e.code === "ERROR_CEREMONY_ABORTED") {
				return null;
			} else if (e instanceof WebAuthnError && e.name === "NotAllowedError") {
				if (retries > 3) return null;
				retries++;
				return startAuthenticationLoop();
			} else if (e instanceof Error && e.name === "AbortError") {
				return null;
			} else {
				reportError(e);
				return null;
			}
		}
	};

	const authenticationResponse = await startAuthenticationLoop();

	if (!authenticationResponse) return;

	submitting.value = true;

	const verificationResult = await verifyPublicKeyCredentialAuthenticationMutate({
		input: {
			authenticationResponse: authenticationResponse as unknown as Record<string, unknown>,
			requestId,
		},
	});

	const jwt = verificationResult?.data?.verifyPublicKeyCredentialAuthentication.jwt;

	if (!jwt) {
		throw new Error("Authentication failed");
	}

	try {
		await onLogin(jwt);
	} catch (e: unknown) {
		reportError(e);
		return;
	} finally {
		submitting.value = false;
	}

	if (authenticationResponse.authenticatorAttachment === "cross-platform") {
		submitting.value = true;
		await performWebAuthnAutoregister();
	}

	void sendPasskeySignals(optionsJSON.rpId);

	await router.push(redirect.value || "/");
}

onMounted(async () => {
	// Check WebAuthn support
	browserSupportsWebAuthnState.value = browserSupportsWebAuthn();

	// Fetch authentication options
	try {
		await fetchWebAuthnAuthenticationOptions();
		void performWebAuthnAutoFill();
	} catch (e) {
		reportError(e, "Failed to initialize WebAuthn:");
	}
});

onUnmounted(() => {
	if (WebAuthnAbortService) {
		WebAuthnAbortService.cancelCeremony();
	}
});
</script>
